/**
 * Camada 1c · a fonte de conhecimento, escolhida em UM lugar para a gaveta e para a Central.
 *
 * O DEFEITO QUE ISTO FECHA, medido em 05/09/2026: dentro do MESMO app, a gaveta de busca
 * rápida (`PainelAjuda`) já falava HTTP com `services/knowledge`, enquanto a Central de Ajuda
 * usava `criarFonteCentralEmMemoria()` (no app de um produto ja removido). Duas superfícies
 * do mesmo produto, lendo bases diferentes: o banco tinha 13 categorias e 95 artigos, e a
 * Central mostrava 8 categorias de um arquivo do front. As duas telas contavam histórias
 * diferentes, e nenhuma das duas estava errada do seu próprio ponto de vista.
 *
 * A REGRA DA CASA É QUE A FONTE DA VERDADE É UMA. Aqui ela passa a ser: quem escolhe é este
 * arquivo, e ele escolhe uma vez para as duas.
 *
 * A DEGRADAÇÃO É DECLARADA, E NÃO ESCONDIDA. Enquanto os sete métodos de `FonteCentral` não
 * existirem no serviço, a memória continua atendendo o que falta. O que muda em relação a hoje
 * é que isso deixa de ser silencioso: `modo` diz em qual estado a fonte está, e a tela mostra.
 * Ausência de dado se diz, nunca se preenche.
 */
import { useMemo } from 'react';
import {
  criarFonteAjudaHttp,
  criarFonteCentralEmMemoria,
  criarFonteCentralHttp,
  type FonteAjuda,
  type FonteCentral,
} from '@complexity/help';
import { BASES } from './bases.js';
import type { Credenciais } from './credenciais.js';

/**
 * Em que estado a base de conhecimento está para este app, agora.
 *
 * ELE EXISTE PARA A TELA DIZER, e não para o código decidir. Um app que não sabe em qual dos
 * três está acaba mostrando lista vazia com cara de "não há artigos", quando o que houve foi
 * falta de configuração.
 */
export type ModoDaAjuda =
  /** Serviço configurado e respondendo. A busca é do banco. */
  | 'servico'
  /**
   * Serviço configurado, e a Central ainda usa memória para o que o serviço não expõe.
   *
   * É o estado de HOJE, e ele é temporário: `services/knowledge` cobre `buscar` e `corpo`, e
   * os outros cinco métodos de `FonteCentral` (categorias, artigos da categoria, e as três de
   * conversa) ainda não existem lá.
   */
  | 'parcial'
  /** Sem `VITE_CX_API_KNOWLEDGE`. Tudo vem da memória, e a tela precisa dizer isso. */
  | 'memoria';

export interface AjudaMontada {
  /** A gaveta de busca rápida do cabeçalho. */
  readonly fonteDaGaveta: FonteAjuda;
  /** A Central de Ajuda, a tela inteira com categorias e conversas. */
  readonly fonteDaCentral: FonteCentral;
  /** Em qual dos três estados a base está. A tela mostra, e não esconde. */
  readonly modo: ModoDaAjuda;
}

/**
 * Monta as duas fontes de uma vez.
 *
 * `useMemo` PELA MESMA RAZÃO DE `credenciais.ts`: as fontes são dependência de efeito das duas
 * telas, e recriá-las a cada render dispara busca em laço.
 */
export function usarFonteDeAjuda(credenciais: Credenciais, produtoId: string): AjudaMontada {
  return useMemo(() => {
    const temServico = BASES.conhecimento !== undefined;

    // A GAVETA JÁ FALA HTTP HOJE, e continua falando. Sem base, `criarFonteAjudaHttp` recebe
    // `undefined` e a própria função de `packages/help` trata isso devolvendo estado de
    // "fonte não configurada", que é o comportamento correto e já existente.
    const fonteDaGaveta = criarFonteAjudaHttp(BASES.conhecimento, {
      obter: () => {
        const { token, tenantId } = credenciais.obter();
        return { token: token ?? undefined, tenantId: tenantId ?? undefined };
      },
    });

    // A CENTRAL SAIU DA MEMÓRIA EM 06/09/2026, e a condição para isso era uma só: os SETE
    // métodos de `FonteCentral` existirem no serviço. Medidos um a um contra o banco, com
    // dado real: categorias 13, buscar 13, artigosDaCategoria 8, corpo 200, minhasConversas
    // 10, mensagens do fio 4, não lidas 14.
    //
    // AS DUAS BASES, e não uma: o conhecimento mora em `services/knowledge` e a conversa em
    // `services/messaging`. Duplicar a conversa no knowledge faria dois serviços escreverem
    // nas mesmas tabelas, e o sino passaria a discordar da Central sobre a mesma conversa.
    //
    // A MEMÓRIA CONTINUA EXISTINDO como degradação, e ela é DECLARADA: sem base configurada,
    // a tela diz em qual estado está em vez de mostrar lista vazia com cara de "não há nada".
    const fonteDaCentral = temServico
      ? criarFonteCentralHttp({
          produtoId,
          conhecimento: BASES.conhecimento,
          mensagens: BASES.mensagens,
          credenciais: {
            obter: () => {
              const { token, tenantId } = credenciais.obter();
              return { token: token ?? undefined, tenantId: tenantId ?? undefined };
            },
          },
        })
      : criarFonteCentralEmMemoria();

    return {
      fonteDaGaveta,
      fonteDaCentral,
      // `servico` só quando as DUAS bases existem: com a de mensagens ausente, as três de
      // conversa não têm com quem falar, e chamar isso de "serviço" seria mentir sobre o que
      // a tela consegue fazer.
      modo: !temServico ? 'memoria' : BASES.mensagens === undefined ? 'parcial' : 'servico',
    };
  }, [credenciais, produtoId]);
}
